import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase, fullNameLastFirst } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import ProfileTabBar from '../../components/ProfileTabBar';
import DataTable from '../../components/DataTable';

// Same Supabase project as the caregiver mobile app — this page manages
// rooms/room_members/bulletins directly, no separate backend. Single space
// for now (MVP), matching the mobile app's own current scope. "Space" is
// user-facing terminology only — the underlying rooms/room_members tables
// keep their names, same as Google Chat's own Room→Space rename didn't
// require anyone to touch a database.

const invokeInvite = async (body) => {
  const { data, error } = await supabase.functions.invoke('invite-staff', { body });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
  return data;
};

// Same palette + hash as the mobile app's Avatar component (src/components/
// avatar.tsx in the caregiver-app repo) — a given Space/DM gets the same
// color in both places, since both hash the same room id.
const AVATAR_PALETTE = ['#E8B896', '#8FB8D8', '#A8C99A', '#D9A6C2', '#C9B896', '#9BC3C9', '#D8A88A', '#B3AEDB'];
const colorForSeed = (seed) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};
const initialsFor = (name) => {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};
// Squircle = Space (group), circle = Direct Message — same shape
// convention as the mobile app, so it reads the same in both places.
// `emoji` is Space-only (an admin-chosen custom avatar, see Space
// Members' rename card) — always omitted for DMs, which stay initials
// since a DM is a real person.
const SpaceAvatar = ({ name, seed, isDm, size = 26, emoji }) => (
  <div style={{
    width: size, height: size, borderRadius: isDm ? size / 2 : size * 0.3, flexShrink: 0,
    background: colorForSeed(seed), color: 'var(--navy)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: emoji ? size * 0.56 : size * 0.4, fontWeight: 700,
  }}>
    {emoji || initialsFor(name)}
  </div>
);

const priorityColor = (p) => ({
  urgent:    { bg: 'var(--danger-bg)', color: 'var(--danger)' },
  important: { bg: 'var(--warn-bg)',   color: 'var(--warn)' },
  normal:    { bg: 'var(--bg2)',       color: 'var(--text3)' },
}[p] || { bg: 'var(--bg2)', color: 'var(--text3)' });

// Same semantics as the mobile app's setMuted — 'tomorrow' means next local
// midnight (quiet till morning), not a fixed 24h from now.
function snoozeUntil(duration) {
  const now = new Date();
  if (duration === '1h') return new Date(now.getTime() + 60 * 60 * 1000);
  if (duration === '8h') return new Date(now.getTime() + 8 * 60 * 60 * 1000);
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
}

// Coarse "last seen" phrasing, same convention as the mobile app's timeAgo.
function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function CaregiverApp() {
  const [tab, setTab] = useState('members');
  return (
    <div>
      <div style={{ background: 'var(--accent-bg)', border: '0.5px solid var(--accent-border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 12, color: 'var(--accent2)' }}>
        Manages the Caera Caregiver mobile app (chat + bulletins) — same Supabase project as the CRM, no separate login.
      </div>
      <ProfileTabBar
        tabs={[
          { key: 'chat', label: 'Chat' },
          { key: 'members', label: 'Space Members' },
          { key: 'bulletins', label: 'Bulletins' },
        ]}
        activeKey={tab}
        onSelect={setTab}
      />
      {tab === 'chat' ? <ChatTab /> : tab === 'members' ? <MembersTab /> : <BulletinsTab />}
    </div>
  );
}

const QUICK_EMOJI = ['👍', '❤️', '✅', '😂', '🙏'];
const MESSAGE_SELECT = 'id,room_id,sender_id,body,created_at,edited_at,hidden_at,reply_to_message_id,pinned_at,profiles!messages_sender_id_fkey(full_name)';

// Batches reactions + attachments for a page of messages in two queries —
// same shape as the mobile app's attachReactionsAndFiles, kept here rather
// than shared since the CRM and caregiver-app are deliberately separate
// codebases (no shared package between them).
async function attachReactionsAndFiles(rows) {
  const ids = rows.map(r => r.id);
  if (!ids.length) return rows;
  const [{ data: reactionRows }, { data: attachmentRows }] = await Promise.all([
    supabase.from('message_reactions').select('message_id,emoji,profile_id,profiles(full_name)').in('message_id', ids),
    supabase.from('message_attachments').select('id,message_id,storage_path,content_type,size_bytes').in('message_id', ids),
  ]);
  const reactionsByMsg = {};
  (reactionRows || []).forEach(r => { (reactionsByMsg[r.message_id] ??= []).push(r); });
  const attachmentsByMsg = {};
  (attachmentRows || []).forEach(a => { (attachmentsByMsg[a.message_id] ??= []).push(a); });
  return rows.map(r => ({ ...r, reactions: reactionsByMsg[r.id] || [], attachments: attachmentsByMsg[r.id] || [] }));
}

// ── Tab 0: Chat — read/post rooms, and start admin-initiated 1:1 DMs ─────
function ChatTab() {
  const { profile } = useAuth();
  const [groupRooms, setGroupRooms] = useState([]);
  const [dmRooms, setDmRooms] = useState([]); // [{id, otherName}]
  const [roomId, setRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDmPicker, setShowDmPicker] = useState(false);
  const [dmCandidates, setDmCandidates] = useState([]);
  const [dmSearch, setDmSearch] = useState('');
  const [startingDm, setStartingDm] = useState(false);
  const [search, setSearch] = useState('');
  const [pinned, setPinned] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [roomMembers, setRoomMembers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [pendingMentions, setPendingMentions] = useState([]);
  const [forwardTarget, setForwardTarget] = useState(null);
  const [showMuteMenu, setShowMuteMenu] = useState(false);
  const [mutedUntil, setMutedUntil] = useState(null);
  const [globalSearch, setGlobalSearch] = useState(false);
  const [globalResults, setGlobalResults] = useState([]);
  const [activeProfileIds, setActiveProfileIds] = useState([]);
  const [otherLastActive, setOtherLastActive] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const isAdmin = profile?.role === 'admin';

  const toast = (msg, isError = false) => {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const loadRooms = useCallback(async () => {
    if (!profile) return;
    const { data: allRooms } = await supabase.from('rooms').select('id,name,is_dm,avatar_emoji').order('name');
    const group = (allRooms || []).filter(r => !r.is_dm);
    const dms = (allRooms || []).filter(r => r.is_dm);

    let resolvedDms = [];
    if (dms.length) {
      const { data: members } = await supabase.from('room_members')
        .select('room_id, profiles(full_name)')
        .in('room_id', dms.map(r => r.id))
        .neq('profile_id', profile.id);
      const otherNameByRoom = {};
      (members || []).forEach(m => { otherNameByRoom[m.room_id] = m.profiles?.full_name || 'Direct Message'; });
      resolvedDms = dms.map(r => ({ id: r.id, otherName: otherNameByRoom[r.id] || 'Direct Message' }))
        .sort((a, b) => a.otherName.localeCompare(b.otherName));
    }

    setGroupRooms(group);
    setDmRooms(resolvedDms);
    setRoomId(prev => prev || group[0]?.id || resolvedDms[0]?.id || null);
  }, [profile]);

  useEffect(() => { loadRooms(); }, [loadRooms]);

  const loadPinned = useCallback(async () => {
    if (!roomId) { setPinned(null); return; }
    const { data } = await supabase.from('messages').select(MESSAGE_SELECT)
      .eq('room_id', roomId).not('pinned_at', 'is', null).order('pinned_at', { ascending: false }).limit(1).maybeSingle();
    setPinned(data || null);
  }, [roomId]);

  useEffect(() => {
    loadPinned();
    if (!roomId || !profile) { setIsMuted(false); setMutedUntil(null); return; }
    supabase.from('room_members').select('muted,muted_until').eq('room_id', roomId).eq('profile_id', profile.id).maybeSingle()
      .then(({ data }) => {
        const snoozedActive = data?.muted_until && new Date(data.muted_until) > new Date();
        setIsMuted(Boolean(data?.muted) || Boolean(snoozedActive));
        setMutedUntil(snoozedActive ? data.muted_until : null);
      });
  }, [roomId, profile, loadPinned]);

  useEffect(() => {
    if (!roomId) { setRoomMembers([]); return; }
    supabase.from('room_members').select('profiles(id, full_name)').eq('room_id', roomId)
      .then(({ data }) => setRoomMembers((data || []).map(m => m.profiles).filter(Boolean)));
  }, [roomId]);

  // Presence: who has this Space/DM open right now — same channel name as
  // the mobile app (presence:<roomId>), so an admin viewing here and a
  // caregiver in the mobile app see each other as active, not two separate
  // presence sets. Also touches profiles.last_active_at periodically, the
  // durable "last seen" fallback for whoever isn't in the live set.
  useEffect(() => {
    if (!roomId || !profile) return;
    const ch = supabase.channel(`presence:${roomId}`);
    // Deriving ids from the tracked payloads (not Object.keys(state)) —
    // every client sharing the literal '' presence key used to collapse
    // everyone, including yourself, onto that one key, so the count was
    // always 1 the instant anyone opened the room. Verified live: this is
    // exactly why a DM's other participant showed "Active now" the moment
    // you opened it, defeating "last seen" for that case entirely.
    ch.on('presence', { event: 'sync' }, () => {
      const state = ch.presenceState();
      const ids = new Set();
      Object.values(state).forEach(entries => entries.forEach(e => { if (e.profileId) ids.add(e.profileId); }));
      setActiveProfileIds(Array.from(ids));
    });
    ch.subscribe(async (status) => { if (status === 'SUBSCRIBED') await ch.track({ profileId: profile.id }); });
    supabase.from('profiles').update({ last_active_at: new Date().toISOString() }).eq('id', profile.id);
    const interval = setInterval(() => {
      supabase.from('profiles').update({ last_active_at: new Date().toISOString() }).eq('id', profile.id);
    }, 120_000);
    return () => { supabase.removeChannel(ch); clearInterval(interval); };
  }, [roomId, profile]);

  const currentIsDm = dmRooms.some(r => r.id === roomId);
  // The *other* person specifically, not just "is the active set non-empty"
  // — I'm always in that set myself the moment I track presence for this
  // room, which would otherwise show "Active now" for someone who was
  // never actually there (verified live before this fix).
  const otherMember = roomMembers.find(m => m.id !== profile?.id);
  const otherIsActive = currentIsDm && Boolean(otherMember) && activeProfileIds.includes(otherMember.id);

  useEffect(() => {
    if (!currentIsDm || otherIsActive || !otherMember) { setOtherLastActive(null); return; }
    supabase.from('profiles').select('last_active_at').eq('id', otherMember.id).maybeSingle()
      .then(({ data }) => setOtherLastActive(data?.last_active_at ?? null));
  }, [currentIsDm, otherIsActive, otherMember]);

  const headerSub = currentIsDm
    ? (otherIsActive ? 'Active now' : otherLastActive ? `Last seen ${timeAgo(otherLastActive)}` : null)
    : null;

  const handleDraftChange = (text) => {
    setDraft(text);
    const match = text.match(/@([\w ]{0,20})$/);
    setMentionQuery(match ? match[1].toLowerCase() : null);
  };

  const selectMention = (member) => {
    setDraft(prev => prev.replace(/@([\w ]{0,20})$/, `@${member.full_name} `));
    setPendingMentions(prev => (prev.some(m => m.id === member.id) ? prev : [...prev, member]));
    setMentionQuery(null);
  };

  const mentionCandidates = mentionQuery === null ? [] :
    roomMembers.filter(m => m.full_name?.toLowerCase().includes(mentionQuery)).slice(0, 6);

  const openDmPicker = async () => {
    const { data } = await supabase.from('profiles').select('id, full_name, email').eq('active', true).neq('id', profile.id).order('full_name');
    setDmCandidates(data || []);
    setDmSearch('');
    setShowDmPicker(true);
  };

  const startOrOpenDm = async (target) => {
    setStartingDm(true);
    setError('');
    try {
      // Reuse an existing DM if one already exists between these two people
      // rather than creating a duplicate every time the picker's used.
      const { data: theirDmRooms } = await supabase.from('room_members')
        .select('room_id, rooms!inner(is_dm)').eq('profile_id', target.id).eq('rooms.is_dm', true);
      let existingRoomId = null;
      if (theirDmRooms?.length) {
        const { data: mine } = await supabase.from('room_members')
          .select('room_id').eq('profile_id', profile.id).in('room_id', theirDmRooms.map(r => r.room_id));
        existingRoomId = mine?.[0]?.room_id || null;
      }

      let newRoomId = existingRoomId;
      if (!newRoomId) {
        const { data: newRoom, error: roomErr } = await supabase.from('rooms')
          .insert({ name: target.full_name, is_dm: true, created_by: profile.id }).select('id').single();
        if (roomErr) throw roomErr;
        newRoomId = newRoom.id;
        await supabase.from('room_members').insert([
          { room_id: newRoomId, profile_id: profile.id },
          { room_id: newRoomId, profile_id: target.id },
        ]);
      }

      setShowDmPicker(false);
      await loadRooms();
      setRoomId(newRoomId);
    } catch (e) {
      setError(e.message);
    } finally {
      setStartingDm(false);
    }
  };

  const filteredCandidates = dmCandidates.filter(c =>
    !dmSearch.trim() || c.full_name?.toLowerCase().includes(dmSearch.trim().toLowerCase()));

  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    setLoading(true);

    // Explicit fkey name required — messages now has three FKs into
    // profiles (sender_id, hidden_by, pinned_by, added by the caregiver
    // app's feature-review migration), so a bare `profiles(...)` embed is
    // ambiguous to PostgREST.
    supabase.from('messages')
      .select(MESSAGE_SELECT)
      .eq('room_id', roomId).order('created_at', { ascending: true }).limit(200)
      .then(async ({ data }) => {
        if (cancelled) return;
        setMessages(await attachReactionsAndFiles(data || []));
        setLoading(false);
      });

    const channel = supabase.channel(`admin-room:${roomId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `room_id=eq.${roomId}` },
        (payload) => {
          // Realtime payload has no joined profile — attach a name for
          // anyone already visible in this room's history, or ourselves via
          // the loaded profile, else fall back to null rather than blank.
          // Own-message case is resolved directly (not just from `prev`) so
          // it doesn't cascade a missing name into every later reply-quote
          // that keys off this row.
          setMessages(prev => {
            if (prev.some(m => m.id === payload.new.id)) return prev;
            const known = prev.find(m => m.sender_id === payload.new.sender_id && m.profiles?.full_name);
            const ownName = payload.new.sender_id === profile?.id ? { full_name: profile.full_name } : null;
            return [...prev, { ...payload.new, profiles: known?.profiles ?? ownName, reactions: [], attachments: [] }];
          });
        })
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages', filter: `room_id=eq.${roomId}` },
        (payload) => {
          // Edit/hide/pin — replace in place, keep whatever reactions/
          // attachments/profile join this row already had locally.
          setMessages(prev => prev.map(m => (m.id === payload.new.id ? { ...m, ...payload.new } : m)));
          if (payload.new.pinned_at) loadPinned();
        })
      .subscribe();

    // Reactions have no room_id column to filter by — unfiltered, dropped
    // client-side for any message_id not currently loaded in this room.
    const reactionChannel = supabase.channel(`admin-reactions:${roomId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'message_reactions' }, (payload) => {
        setMessages(prev => prev.map(m => (m.id === payload.new.message_id
          ? { ...m, reactions: [...(m.reactions || []), payload.new] } : m)));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'message_reactions' }, (payload) => {
        setMessages(prev => prev.map(m => (m.id === payload.old.message_id
          ? { ...m, reactions: (m.reactions || []).filter(r => !(r.profile_id === payload.old.profile_id && r.emoji === payload.old.emoji)) } : m)));
      })
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(channel); supabase.removeChannel(reactionChannel); };
  }, [roomId, loadPinned]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || !roomId) return;
    if (editingId) {
      setSending(true);
      const { error: err } = await supabase.from('messages').update({ body, edited_at: new Date().toISOString() }).eq('id', editingId);
      setSending(false);
      if (err) { setError(err.message); return; }
      setMessages(prev => prev.map(m => (m.id === editingId ? { ...m, body, edited_at: new Date().toISOString() } : m)));
      setEditingId(null);
      setDraft('');
      return;
    }
    setSending(true);
    setError('');
    const mentionedProfileIds = pendingMentions.filter(m => body.includes(`@${m.full_name}`)).map(m => m.id);
    const { error: err } = await supabase.from('messages')
      .insert({ room_id: roomId, sender_id: profile.id, body, reply_to_message_id: replyTo?.id ?? null, mentioned_profile_ids: mentionedProfileIds });
    setSending(false);
    if (err) { setError(err.message); return; }
    setDraft('');
    setPendingMentions([]);
    setMentionQuery(null);
    setReplyTo(null);
  };

  const toggleReaction = async (message, emoji) => {
    const mine = (message.reactions || []).some(r => r.profile_id === profile.id && r.emoji === emoji);
    setMessages(prev => prev.map(m => (m.id !== message.id ? m : {
      ...m,
      reactions: mine
        ? m.reactions.filter(r => !(r.profile_id === profile.id && r.emoji === emoji))
        : [...(m.reactions || []), { profile_id: profile.id, emoji, message_id: message.id }],
    })));
    if (mine) await supabase.from('message_reactions').delete().eq('message_id', message.id).eq('profile_id', profile.id).eq('emoji', emoji);
    else await supabase.from('message_reactions').insert({ message_id: message.id, profile_id: profile.id, emoji }).then(({ error: err }) => {
      if (err && err.code !== '23505') setError(err.message);
    });
  };

  const togglePin = async (message) => {
    const pin = !message.pinned_at;
    await supabase.rpc('pin_message', { _message_id: message.id, _pin: pin });
    setMessages(prev => prev.map(m => (m.id === message.id ? { ...m, pinned_at: pin ? new Date().toISOString() : null } : m)));
    loadPinned();
  };

  const hideMessage = async (message) => {
    if (message.sender_id === profile.id) {
      await supabase.from('messages').update({ hidden_at: new Date().toISOString(), hidden_by: profile.id }).eq('id', message.id);
    } else {
      await supabase.rpc('admin_hide_message', { _message_id: message.id });
    }
    setMessages(prev => prev.map(m => (m.id === message.id ? { ...m, hidden_at: new Date().toISOString() } : m)));
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !roomId) return;
    setUploading(true);
    setError('');
    try {
      const { data: sent, error: sendErr } = await supabase.from('messages')
        .insert({ room_id: roomId, sender_id: profile.id, body: `📎 ${file.name}` })
        .select(MESSAGE_SELECT).single();
      if (sendErr) throw sendErr;
      const path = `${roomId}/${crypto.randomUUID()}-${file.name}`;
      const { error: uploadErr } = await supabase.storage
        .from('message-attachments').upload(path, file, { contentType: file.type });
      if (uploadErr) throw uploadErr;
      const { data: attachment, error: attErr } = await supabase.from('message_attachments')
        .insert({ message_id: sent.id, storage_path: path, content_type: file.type, size_bytes: file.size, uploaded_by: profile.id })
        .select('id,message_id,storage_path,content_type,size_bytes').single();
      if (attErr) throw attErr;
      setMessages(prev => (prev.some(m => m.id === sent.id)
        ? prev.map(m => (m.id === sent.id ? { ...m, attachments: [...(m.attachments || []), attachment] } : m))
        : [...prev, { ...sent, reactions: [], attachments: [attachment] }]));
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // duration: 'forever' | '1h' | '8h' | 'tomorrow' | false (unmute). Kept
  // consistent with the mobile app's setMuted — a temporary snooze
  // (muted_until) layered on top of the existing permanent `muted` boolean
  // rather than replacing it.
  const setMuteDuration = async (duration) => {
    setShowMuteMenu(false);
    const patch = duration === false ? { muted: false, muted_until: null }
      : duration === 'forever' ? { muted: true, muted_until: null }
      : { muted: false, muted_until: snoozeUntil(duration).toISOString() };
    await supabase.from('room_members').update(patch).eq('room_id', roomId).eq('profile_id', profile.id);
    setIsMuted(duration !== false);
    setMutedUntil(patch.muted_until);
  };

  const copyMessage = (body) => {
    navigator.clipboard?.writeText(body).catch(() => toast('Could not copy message', true));
  };

  const forwardTo = async (targetRoomId, targetLabel) => {
    if (!forwardTarget) return;
    const message = forwardTarget;
    setForwardTarget(null);
    const { error: err } = await supabase.from('messages').insert({
      room_id: targetRoomId, sender_id: profile.id,
      body: `↪️ Forwarded from ${message.profiles?.full_name || 'someone'}\n${message.body}`,
    });
    if (err) { toast(err.message, true); return; }
    toast(`Forwarded to ${targetLabel}`);
  };

  // Global mode swaps the source entirely — searching everywhere means
  // querying across every Space/DM the admin can see (RLS-scoped, same as
  // any other query here), not just filtering the one room already loaded.
  useEffect(() => {
    if (!globalSearch || !search.trim()) { setGlobalResults([]); return; }
    const q = search.trim();
    const handle = setTimeout(() => {
      supabase.from('messages').select(MESSAGE_SELECT + ',rooms(name,is_dm)')
        .is('hidden_at', null).ilike('body', `%${q}%`)
        .order('created_at', { ascending: false }).limit(50)
        .then(({ data }) => setGlobalResults((data || []).map(m => ({
          ...m, spaceName: m.rooms?.is_dm ? 'Direct Message' : (m.rooms?.name || 'Space'),
        }))));
    }, 300);
    return () => clearTimeout(handle);
  }, [globalSearch, search]);

  const filteredMessages = search.trim()
    ? messages.filter(m => !m.hidden_at && m.body.toLowerCase().includes(search.trim().toLowerCase()))
    : messages;
  const messageById = Object.fromEntries(messages.map(m => [m.id, m]));

  const activeRoomLabel = groupRooms.find(r => r.id === roomId)?.name
    || dmRooms.find(r => r.id === roomId)?.otherName || 'Chat';

  return (
    <div className="card" style={{ display: 'flex', height: 480, overflow: 'hidden' }}>
      {/* Space list sidebar */}
      <div style={{ width: 220, borderRight: '0.5px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {groupRooms.length > 0 && (
            <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '4px 8px 4px' }}>Spaces</div>
          )}
          {groupRooms.map(r => (
            <RoomListItem key={r.id} active={r.id === roomId} label={r.name} seed={r.id} isDm={false} emoji={r.avatar_emoji} onClick={() => setRoomId(r.id)} />
          ))}
          {dmRooms.length > 0 && (
            <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '10px 8px 4px' }}>Direct Messages</div>
          )}
          {dmRooms.map(r => (
            <RoomListItem key={r.id} active={r.id === roomId} label={r.otherName} seed={r.id} isDm onClick={() => setRoomId(r.id)} />
          ))}
        </div>
        <div style={{ padding: 8, borderTop: '0.5px solid var(--border)' }}>
          <button className="btn btn-outline btn-sm" style={{ width: '100%', fontSize: 11 }} onClick={openDmPicker}>+ New Direct Message</button>
        </div>
      </div>

      {/* Chat panel */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div className="card-header" style={{ alignItems: 'flex-start' }}>
          <div>
            <div className="card-title">{activeRoomLabel}</div>
            {headerSub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{headerSub}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text3)' }}>
              <input type="checkbox" checked={globalSearch} onChange={e => setGlobalSearch(e.target.checked)} />
              Everywhere
            </label>
            <input
              className="form-input" style={{ width: 160, fontSize: 12, height: 28 }}
              placeholder={globalSearch ? 'Search all messages…' : 'Search this space…'}
              value={search} onChange={e => setSearch(e.target.value)}
            />
            <div style={{ position: 'relative' }}>
              <button
                type="button" title={isMuted ? 'Muted — click to change' : 'Mute this space'} onClick={() => setShowMuteMenu(v => !v)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15 }}
              >{isMuted ? '🔕' : '🔔'}</button>
              {showMuteMenu && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, zIndex: 20, marginTop: 4,
                  background: 'var(--topbar-bg)', border: '1px solid var(--border)', borderRadius: 8,
                  boxShadow: '0 4px 16px rgba(13,43,78,0.12)', minWidth: 150, overflow: 'hidden',
                }}>
                  {[['1h', 'For 1 hour'], ['8h', 'For 8 hours'], ['tomorrow', 'Until tomorrow'], ['forever', 'Forever']].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setMuteDuration(val)} style={muteMenuItemStyle}>{label}</button>
                  ))}
                  {isMuted && <button type="button" onClick={() => setMuteDuration(false)} style={muteMenuItemStyle}>Unmute</button>}
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>Posting as {profile?.full_name}</div>
          </div>
        </div>

        {pinned && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: 'var(--accent-bg)', borderBottom: '0.5px solid var(--border)' }}>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--accent2)', flexShrink: 0 }}>📌 Pinned</span>
            <span style={{ fontSize: 12, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {pinned.hidden_at ? 'Message removed' : pinned.body}
            </span>
            <button type="button" onClick={() => togglePin(pinned)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 12 }}>✕</button>
          </div>
        )}

        {success && <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success-border)', borderRadius: 8, padding: '10px 14px', margin: '0 16px 12px', fontSize: 12, color: 'var(--success)' }}>✓ {success}</div>}
        {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', margin: '0 16px 12px', fontSize: 12, color: 'var(--danger)' }}>{error}</div>}

        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {globalSearch && search.trim() ? (
            globalResults.length === 0 ? (
              <div className="empty-state">No matches.</div>
            ) : globalResults.map(m => (
              <button
                key={m.id} type="button"
                onClick={() => { setRoomId(m.room_id); setGlobalSearch(false); setSearch(''); }}
                style={{ textAlign: 'left', border: '0.5px solid var(--border)', borderRadius: 10, padding: 10, background: 'var(--surface)', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>
                  <span style={{ fontWeight: 700 }}>{m.profiles?.full_name || 'Unknown'} · {m.spaceName}</span>
                  <span>{new Date(m.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text)' }}>{m.body}</div>
              </button>
            ))
          ) : !roomId ? (
            <div className="empty-state">No spaces yet.</div>
          ) : loading ? (
            <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
          ) : filteredMessages.length === 0 ? (
            <div className="empty-state">{search.trim() ? 'No matches.' : 'No messages in this space yet.'}</div>
          ) : (
            filteredMessages.map(m => {
              const mine = m.sender_id === profile?.id;
              const hidden = Boolean(m.hidden_at);
              const canEdit = mine;
              const canHide = mine || isAdmin;
              const replySource = m.reply_to_message_id ? messageById[m.reply_to_message_id] : null;
              const grouped = {};
              (m.reactions || []).forEach(r => { (grouped[r.emoji] ??= []).push(r); });
              return (
                <div
                  key={m.id}
                  style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '70%', position: 'relative' }}
                  onMouseEnter={() => setOpenMenuId(m.id)}
                  onMouseLeave={() => setOpenMenuId(prev => (prev === m.id ? null : prev))}
                >
                  {!mine && <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>{m.profiles?.full_name || 'Unknown'}</div>}
                  {replySource && (
                    <div style={{ borderLeft: '3px solid var(--accent)', background: 'var(--bg2)', borderRadius: '0 6px 6px 0', padding: '4px 8px', marginBottom: 3, fontSize: 11 }}>
                      <div style={{ fontWeight: 700, color: 'var(--text3)' }}>{replySource.profiles?.full_name || 'Someone'}</div>
                      <div style={{ color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{replySource.body}</div>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    {mine && openMenuId === m.id && !hidden && (
                      <MessageHoverMenu
                        canEdit={canEdit} canHide={canHide} isPinned={Boolean(m.pinned_at)}
                        onReact={emoji => toggleReaction(m, emoji)}
                        onReply={() => setReplyTo(m)}
                        onEdit={() => { setEditingId(m.id); setDraft(m.body); }}
                        onHide={() => hideMessage(m)}
                        onPin={() => togglePin(m)}
                        onCopy={() => copyMessage(m.body)}
                        onForward={() => setForwardTarget(m)}
                      />
                    )}
                    <div>
                      <div style={{
                        padding: '8px 12px', borderRadius: 12,
                        background: mine ? 'var(--accent)' : 'var(--bg2)',
                        color: mine ? '#fff' : 'var(--text)',
                        fontSize: 13, lineHeight: 1.4, fontStyle: hidden ? 'italic' : 'normal',
                      }}>
                        {hidden ? 'Message removed' : m.body}
                      </div>
                      {m.attachments?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                          {m.attachments.map(a => <AttachmentChip key={a.id} attachment={a} />)}
                        </div>
                      )}
                    </div>
                    {!mine && openMenuId === m.id && !hidden && (
                      <MessageHoverMenu
                        canEdit={canEdit} canHide={canHide} isPinned={Boolean(m.pinned_at)}
                        onReact={emoji => toggleReaction(m, emoji)}
                        onReply={() => setReplyTo(m)}
                        onEdit={() => { setEditingId(m.id); setDraft(m.body); }}
                        onHide={() => hideMessage(m)}
                        onPin={() => togglePin(m)}
                        onCopy={() => copyMessage(m.body)}
                        onForward={() => setForwardTarget(m)}
                      />
                    )}
                  </div>
                  {Object.keys(grouped).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                      {Object.entries(grouped).map(([emoji, list]) => {
                        const isMine = list.some(r => r.profile_id === profile?.id);
                        return (
                          <button
                            key={emoji} type="button" onClick={() => toggleReaction(m, emoji)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, borderRadius: 20,
                              padding: '2px 8px', border: '0.5px solid var(--border)', cursor: 'pointer',
                              background: isMine ? 'var(--accent-bg)' : 'var(--bg2)', color: 'var(--text)',
                            }}
                          >{emoji} {list.length}</button>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2, textAlign: mine ? 'right' : 'left' }}>
                    {new Date(m.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    {m.edited_at ? ' · edited' : ''}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {(replyTo || editingId) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderTop: '0.5px solid var(--border)', background: 'var(--bg2)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent2)' }}>
                {editingId ? 'Editing message' : `Replying to ${replyTo?.profiles?.full_name || 'message'}`}
              </div>
              {!editingId && <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{replyTo?.body}</div>}
            </div>
            <button type="button" onClick={() => { setReplyTo(null); setEditingId(null); setDraft(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)' }}>✕</button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: '0.5px solid var(--border)' }}>
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelected} />
          <button
            type="button" title="Attach a file" onClick={() => fileInputRef.current?.click()}
            disabled={!roomId || uploading || Boolean(editingId)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text3)', flexShrink: 0 }}
          >{uploading ? '…' : '📎'}</button>
          <div style={{ position: 'relative', flex: 1 }}>
            {mentionCandidates.length > 0 && (
              <div style={{
                position: 'absolute', bottom: '110%', left: 0, right: 0,
                background: 'var(--topbar-bg)', border: '1px solid var(--border)', borderRadius: 8,
                boxShadow: '0 4px 16px rgba(13,43,78,0.12)', zIndex: 100, overflow: 'hidden',
              }}>
                {mentionCandidates.map(m => (
                  <div
                    key={m.id}
                    onMouseDown={e => { e.preventDefault(); selectMention(m); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12.5, color: 'var(--text)', borderBottom: '0.5px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {m.full_name}
                  </div>
                ))}
              </div>
            )}
            <input
              className="form-input" style={{ width: '100%' }}
              value={draft} onChange={e => handleDraftChange(e.target.value)}
              placeholder="Message… (@ to mention)"
              disabled={!roomId}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            />
          </div>
          <button className="btn btn-primary" disabled={sending || !draft.trim() || !roomId} onClick={handleSend}>
            {sending ? '…' : editingId ? 'Save' : 'Send'}
          </button>
        </div>
      </div>

      {showDmPicker && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setShowDmPicker(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <div className="modal-title">Start a Direct Message</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowDmPicker(false)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
              <input
                className="form-input" style={{ marginBottom: 10 }}
                placeholder="Search by name…" value={dmSearch} onChange={e => setDmSearch(e.target.value)} autoFocus
              />
              <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {filteredCandidates.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: 16 }}>No match.</div>
                ) : filteredCandidates.map(c => (
                  <button
                    key={c.id} type="button" disabled={startingDm} onClick={() => startOrOpenDm(c)}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', padding: '8px 10px', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{c.full_name}</span>
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>{c.email}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {forwardTarget && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setForwardTarget(null)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <div className="modal-title">Forward to…</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setForwardTarget(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {[...groupRooms.filter(r => r.id !== roomId).map(r => ({ id: r.id, label: r.name })),
                  ...dmRooms.filter(r => r.id !== roomId).map(r => ({ id: r.id, label: r.otherName }))]
                  .map(target => (
                    <button
                      key={target.id} type="button" onClick={() => forwardTo(target.id, target.label)}
                      style={{ textAlign: 'left', padding: '8px 10px', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: 'var(--text)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {target.label}
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoomListItem({ active, label, seed, isDm, emoji, onClick }) {
  return (
    <button
      type="button" onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
        padding: '6px 8px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
        background: active ? 'var(--accent-bg)' : 'transparent',
        color: active ? 'var(--accent2)' : 'var(--text)',
        fontSize: 13, fontWeight: active ? 500 : 400, marginBottom: 2,
      }}
    >
      <SpaceAvatar name={label} seed={seed} isDm={isDm} emoji={emoji} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
}

// Desktop equivalent of the mobile app's long-press action sheet — a
// hover-revealed row of icon buttons next to the bubble, since "long
// press" isn't a mouse-driven interaction.
function MessageHoverMenu({ canEdit, canHide, isPinned, onReact, onReply, onEdit, onHide, onPin, onCopy, onForward }) {
  const [showEmoji, setShowEmoji] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2, paddingTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 1, background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 8, padding: 2 }}>
        <button type="button" title="React" onClick={() => setShowEmoji(v => !v)} style={iconBtnStyle}>🙂</button>
        <button type="button" title="Copy" onClick={onCopy} style={iconBtnStyle}>📋</button>
        <button type="button" title="Forward" onClick={onForward} style={iconBtnStyle}>↪️</button>
        <button type="button" title="Reply" onClick={onReply} style={iconBtnStyle}>↩️</button>
        <button type="button" title={isPinned ? 'Unpin' : 'Pin'} onClick={onPin} style={iconBtnStyle}>📌</button>
        {canEdit && <button type="button" title="Edit" onClick={onEdit} style={iconBtnStyle}>✏️</button>}
        {canHide && <button type="button" title="Remove" onClick={onHide} style={iconBtnStyle}>🗑️</button>}
      </div>
      {showEmoji && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 5, display: 'flex', gap: 2, background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 8, padding: 4, marginTop: 2 }}>
          {QUICK_EMOJI.map(emoji => (
            <button key={emoji} type="button" onClick={() => { onReact(emoji); setShowEmoji(false); }} style={{ ...iconBtnStyle, fontSize: 15 }}>{emoji}</button>
          ))}
        </div>
      )}
    </div>
  );
}
const iconBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 4, borderRadius: 4, lineHeight: 1 };
const muteMenuItemStyle = {
  display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
  padding: '8px 12px', fontSize: 12.5, color: 'var(--text)', fontFamily: 'inherit',
};

function AttachmentChip({ attachment }) {
  const [url, setUrl] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  useEffect(() => {
    supabase.storage.from('message-attachments').createSignedUrl(attachment.storage_path, 3600)
      .then(({ data }) => setUrl(data?.signedUrl));
  }, [attachment.storage_path]);
  const isImage = attachment.content_type?.startsWith('image/');
  const fileName = attachment.storage_path.split('/').pop()?.replace(/^[0-9a-f-]{36}-/, '') || 'Attachment';
  if (isImage) {
    if (!url) return null;
    return (
      <>
        <img
          src={url} alt={fileName} onClick={() => setLightboxOpen(true)}
          style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8, display: 'block', cursor: 'zoom-in' }}
        />
        {lightboxOpen && (
          <div
            onClick={() => setLightboxOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out',
            }}
          >
            <button
              type="button" onClick={() => setLightboxOpen(false)}
              style={{ position: 'absolute', top: 20, right: 24, background: 'none', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer' }}
            >✕</button>
            <img src={url} alt={fileName} style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }} onClick={e => e.stopPropagation()} />
          </div>
        )}
      </>
    );
  }
  return (
    <a href={url || '#'} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text)', background: 'var(--bg2)', borderRadius: 8, padding: '6px 10px', textDecoration: 'none', width: 'fit-content' }}>
      📄 {fileName}
    </a>
  );
}

// ── Tab 1: Space Members ──────────────────────────────────────────────────
function MembersTab() {
  const [room, setRoom] = useState(null);
  const [members, setMembers] = useState([]);
  const [hasAccountNotInRoom, setHasAccountNotInRoom] = useState([]);
  const [needsInvite, setNeedsInvite] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [nameDraft, setNameDraft] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [emojiDraft, setEmojiDraft] = useState('');
  const [savingEmoji, setSavingEmoji] = useState(false);

  const toast = (msg, isError = false) => {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    // Looked up by is_dm=false (the one non-DM space, MVP scope) rather
    // than matching a hardcoded name string — once a rename is possible, a
    // name-based lookup would stop finding the space the moment someone
    // actually renames it.
    const { data: rooms } = await supabase.from('rooms').select('id,name,avatar_emoji').eq('is_dm', false).limit(1);
    const r = rooms?.[0] || null;
    setRoom(r);
    setNameDraft(r?.name || '');
    setEmojiDraft(r?.avatar_emoji || '');
    if (!r) { setLoading(false); return; }

    // Any active staff member is a valid room candidate — not just
    // caregiver/nurse. Scoping this to those two types meant anyone else
    // with a real login (e.g. a manager) was invisible on this page even
    // though they could plainly be added to room_members directly.
    const [{ data: memberRows }, { data: eligibleStaff }] = await Promise.all([
      supabase.from('room_members').select('profile_id, joined_at, profiles(full_name, email)').eq('room_id', r.id),
      supabase.from('staff').select('id, full_name, first_name, last_name, email, profile_id, position_code, status')
        .eq('status', 'active').order('last_name'),
    ]);

    const memberIds = new Set((memberRows || []).map(m => m.profile_id));
    setMembers(memberRows || []);
    setHasAccountNotInRoom((eligibleStaff || []).filter(s => s.profile_id && !memberIds.has(s.profile_id)));
    setNeedsInvite((eligibleStaff || []).filter(s => !s.profile_id));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRename = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === room.name) return;
    setRenaming(true);
    const { error: err } = await supabase.from('rooms').update({ name: trimmed }).eq('id', room.id);
    setRenaming(false);
    if (err) { toast(err.message, true); return; }
    toast(`Renamed to "${trimmed}"`);
    load();
  };

  // Empty clears back to the computed initials-squircle rather than storing
  // '' — avatar_emoji stays either a real emoji or null, never an empty
  // string, so every reader can keep doing a simple truthy check.
  const saveEmoji = async () => {
    const trimmed = emojiDraft.trim();
    if (trimmed === (room.avatar_emoji || '')) return;
    setSavingEmoji(true);
    const { error: err } = await supabase.from('rooms').update({ avatar_emoji: trimmed || null }).eq('id', room.id);
    setSavingEmoji(false);
    if (err) { toast(err.message, true); return; }
    toast(trimmed ? `Space icon set to ${trimmed}` : 'Space icon cleared');
    load();
  };

  const addToRoom = async (staffRow) => {
    setBusyId(staffRow.id);
    const { error: err } = await supabase.from('room_members').insert({ room_id: room.id, profile_id: staffRow.profile_id });
    setBusyId(null);
    if (err) { toast(err.message, true); return; }
    toast(`${staffRow.full_name} added to ${room.name}`);
    load();
  };

  const removeFromRoom = async (profileId, name) => {
    setBusyId(profileId);
    const { error: err } = await supabase.from('room_members').delete().eq('room_id', room.id).eq('profile_id', profileId);
    setBusyId(null);
    if (err) { toast(err.message, true); return; }
    toast(`${name} removed from ${room.name}`);
    load();
  };

  const confirmInvite = async () => {
    const staffRow = inviteTarget;
    setBusyId(staffRow.id);
    setError('');
    try {
      await invokeInvite({ action: 'invite', full_name: staffRow.full_name, email: staffRow.email, role: 'staff', app: 'caregiver' });
      // invite-staff creates auth.users + profiles but doesn't know about
      // the `staff` table at all — link it back ourselves, and add them to
      // the room in the same step so this is a single admin action.
      const { data: newProfile } = await supabase.from('profiles').select('id').eq('email', staffRow.email).single();
      if (newProfile) {
        await supabase.from('staff').update({ profile_id: newProfile.id }).eq('id', staffRow.id);
        await supabase.from('room_members').insert({ room_id: room.id, profile_id: newProfile.id });
      }
      toast(`${staffRow.full_name} invited and added to ${room.name}`);
      setInviteTarget(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;
  if (!room) return <div className="empty-state">No space found — check migration 0002 ran.</div>;

  return (
    <div>
      {success && <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--success)' }}>✓ {success}</div>}
      {error && !inviteTarget && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--danger)' }}>⚠ {error}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Space Name &amp; Icon</div></div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <SpaceAvatar name={room.name} seed={room.id} isDm={false} emoji={emojiDraft.trim()} size={32} />
            <input
              className="form-input" style={{ flex: 1, maxWidth: 320 }}
              value={nameDraft} onChange={e => setNameDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveRename(); }}
            />
            <button
              className="btn btn-primary btn-sm"
              disabled={renaming || !nameDraft.trim() || nameDraft.trim() === room.name}
              onClick={saveRename}
            >
              {renaming ? 'Saving…' : 'Save'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              className="form-input" style={{ width: 100 }}
              placeholder="🏡" maxLength={8}
              value={emojiDraft} onChange={e => setEmojiDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveEmoji(); }}
              aria-label="Space icon emoji"
            />
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              Optional — replaces the initials avatar everywhere this Space shows up. Leave blank to use initials.
            </span>
            <button
              className="btn btn-outline btn-sm"
              disabled={savingEmoji || emojiDraft.trim() === (room.avatar_emoji || '')}
              onClick={saveEmoji}
            >
              {savingEmoji ? 'Saving…' : 'Save Icon'}
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">{room.name} — Current Members ({members.length})</div></div>
        <DataTable
          tableId="caregiver-app-members"
          columns={[
            { id: 'name', header: 'Name', size: 220, meta: { mobile: 'primary' }, cell: info => fullNameLastFirst(info.row.original.profiles?.full_name) || '—' },
            { id: 'email', header: 'Email', size: 240, meta: { mobile: 'secondary' }, cell: info => info.row.original.profiles?.email || '—' },
            {
              id: 'actions', header: '', size: 100, enableSorting: false, meta: { mobile: 'secondary' },
              cell: info => (
                <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--danger)' }}
                  disabled={busyId === info.row.original.profile_id}
                  onClick={() => removeFromRoom(info.row.original.profile_id, info.row.original.profiles?.full_name || 'this person')}>
                  Remove
                </button>
              ),
            },
          ]}
          data={members}
          getRowId={m => m.profile_id}
          loading={false}
          emptyMessage="No members yet."
          maxHeight="320px"
        />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Have a Login — Not Yet in {room.name} ({hasAccountNotInRoom.length})</div></div>
        <DataTable
          tableId="caregiver-app-not-in-room"
          columns={[
            { id: 'name', header: 'Name', size: 220, meta: { mobile: 'primary' }, cell: info => { const s = info.row.original; return s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name; } },
            { id: 'email', header: 'Email', size: 240, meta: { mobile: 'secondary' }, cell: info => info.row.original.email },
            { accessorKey: 'position_code', header: 'Position', size: 100, meta: { mobile: 'secondary' }, cell: info => <span>{info.getValue() || '—'}</span> },
            {
              id: 'actions', header: '', size: 120, enableSorting: false, meta: { mobile: 'secondary' },
              cell: info => (
                <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }}
                  disabled={busyId === info.row.original.id}
                  onClick={() => addToRoom(info.row.original)}>
                  Add to Space
                </button>
              ),
            },
          ]}
          data={hasAccountNotInRoom}
          getRowId={s => s.id}
          loading={false}
          emptyMessage="Everyone with a login is already in the space."
          maxHeight="320px"
        />
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">Need an Invite — No Login Yet ({needsInvite.length})</div></div>
        <DataTable
          tableId="caregiver-app-needs-invite"
          columns={[
            { id: 'name', header: 'Name', size: 220, meta: { mobile: 'primary' }, cell: info => { const s = info.row.original; return s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name; } },
            { id: 'email', header: 'Email', size: 240, meta: { mobile: 'secondary' }, cell: info => info.row.original.email },
            { accessorKey: 'position_code', header: 'Position', size: 100, meta: { mobile: 'secondary' }, cell: info => <span>{info.getValue() || '—'}</span> },
            {
              id: 'actions', header: '', size: 100, enableSorting: false, meta: { mobile: 'secondary' },
              cell: info => (
                <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => setInviteTarget(info.row.original)}>
                  Invite
                </button>
              ),
            },
          ]}
          data={needsInvite}
          getRowId={s => s.id}
          loading={false}
          emptyMessage="Everyone eligible already has a login."
          maxHeight="320px"
        />
      </div>

      {inviteTarget && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setInviteTarget(null)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <div className="modal-title">Invite {inviteTarget.full_name}?</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setInviteTarget(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
              <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--warn)', lineHeight: 1.5 }}>
                This sends a real welcome email to <strong>{inviteTarget.email}</strong> right now. The email currently points to the CRM login page, not the caregiver app — that copy hasn't been updated yet since the app isn't distributed to real caregivers (still parked pending TestFlight setup). They'll get a working login either way, just with the wrong instructions for now.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setInviteTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmInvite} disabled={busyId === inviteTarget.id}>
                {busyId === inviteTarget.id ? 'Inviting…' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab 2: Bulletins ──────────────────────────────────────────────────────
const emptyDraft = { title: '', body: '', priority: 'normal', requiresAck: false, expiryDays: '' };

function BulletinsTab() {
  const { profile } = useAuth();
  const [bulletins, setBulletins] = useState([]);
  const [ackCounts, setAckCounts] = useState({});
  const [totalActive, setTotalActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(emptyDraft);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const toast = (msg, isError = false) => {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    // bulletins also reaches profiles through bulletin_acks (many-to-many),
    // not just created_by — same PostgREST ambiguity as the Chat tab's
    // messages query, fixed the same way (qualify which relationship to
    // embed). This was silently rendering "All Bulletins (0)" — the error
    // was there in `bulletinsRes.error` all along, just never surfaced.
    const [bulletinsRes, { data: acks }, { count }] = await Promise.all([
      supabase.from('bulletins').select('id,title,body,priority,requires_ack,created_at,expires_at,profiles!bulletins_created_by_fkey(full_name)').order('created_at', { ascending: false }),
      supabase.from('bulletin_acks').select('bulletin_id'),
      supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('active', true),
    ]);
    if (bulletinsRes.error) toast(bulletinsRes.error.message, true);
    const counts = {};
    (acks || []).forEach(a => { counts[a.bulletin_id] = (counts[a.bulletin_id] || 0) + 1; });
    setBulletins(bulletinsRes.data || []);
    setAckCounts(counts);
    setTotalActive(count || 0);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePost = async () => {
    if (!draft.title.trim() || !draft.body.trim()) return;
    setPosting(true);
    setError('');
    const expiresAt = draft.expiryDays
      ? new Date(Date.now() + Number(draft.expiryDays) * 24 * 60 * 60 * 1000).toISOString()
      : null;
    const { error: err } = await supabase.from('bulletins').insert({
      title: draft.title.trim(), body: draft.body.trim(), priority: draft.priority,
      requires_ack: draft.requiresAck, expires_at: expiresAt, created_by: profile?.id,
    });
    setPosting(false);
    if (err) { toast(err.message, true); return; }
    setDraft(emptyDraft);
    toast('Bulletin posted');
    load();
  };

  return (
    <div>
      {success && <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--success)' }}>✓ {success}</div>}
      {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--danger)' }}>⚠ {error}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">New Bulletin</div></div>
        <div style={{ padding: 16, display: 'grid', gap: 12 }}>
          <div>
            <label className="form-label">Title</label>
            <input className="form-input" value={draft.title} onChange={e => setDraft(p => ({ ...p, title: e.target.value }))} placeholder="New CPR wallet card requirement" />
          </div>
          <div>
            <label className="form-label">Message</label>
            <textarea className="form-textarea" rows={3} value={draft.body} onChange={e => setDraft(p => ({ ...p, body: e.target.value }))} placeholder="Upload your current CPR card to the portal by Friday…" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Priority</label>
              <select className="form-select" value={draft.priority} onChange={e => setDraft(p => ({ ...p, priority: e.target.value }))}>
                <option value="normal">Normal</option>
                <option value="important">Important</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="form-label">Expires</label>
              <select className="form-select" value={draft.expiryDays} onChange={e => setDraft(p => ({ ...p, expiryDays: e.target.value }))}>
                <option value="">No expiry</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)', cursor: 'pointer' }}>
                <input type="checkbox" checked={draft.requiresAck} onChange={e => setDraft(p => ({ ...p, requiresAck: e.target.checked }))} />
                Require acknowledgment
              </label>
            </div>
          </div>
          <div>
            <button className="btn btn-primary" disabled={posting || !draft.title.trim() || !draft.body.trim()} onClick={handlePost}>
              {posting ? 'Posting…' : 'Post Bulletin'}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">All Bulletins ({bulletins.length})</div></div>
        {loading ? (
          <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
        ) : bulletins.length === 0 ? (
          <div className="empty-state">No bulletins posted yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {bulletins.map(b => {
              const pc = priorityColor(b.priority);
              const expired = b.expires_at && new Date(b.expires_at) < new Date();
              return (
                <div key={b.id} style={{ padding: '14px 16px', borderBottom: '0.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, opacity: expired ? 0.55 : 1 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{b.title}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 20, background: pc.bg, color: pc.color }}>{b.priority}</span>
                      {expired && <span style={{ fontSize: 10, color: 'var(--text3)' }}>Expired</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>{b.body}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                      {b.profiles?.full_name && <SpaceAvatar name={b.profiles.full_name} seed={b.id} isDm={false} size={16} />}
                      {b.profiles?.full_name ? `${b.profiles.full_name} · ` : ''}{new Date(b.created_at).toLocaleDateString()}
                      {b.requires_ack && ` · ${ackCounts[b.id] || 0} of ${totalActive} acknowledged`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
